/**
 * TaskItem — Single background task row
 *
 * Displays task status, action, phase progress, and action buttons.
 * Supports phase stepper, unviewed indicator, and expandable detail.
 */

import React, { useState, useMemo } from 'react';
import {
  ListItem,
  ListItemText,
  IconButton,
  Box,
  Typography,
  LinearProgress,
  Collapse,
  Chip,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CircleIcon from '@mui/icons-material/Circle';
import { TASKS_PANEL } from '../selectors/tasks-panel.sel';
import { ACTIVE_STATUSES, TERMINAL_STATUSES } from '../types';
import type { BackendTask, TaskPhaseDefinition, TaskRowActionsRenderer } from '../types';

interface TaskItemProps {
  task: BackendTask;
  isUnviewed: boolean;
  compact?: boolean;
  showNavigation?: boolean;
  onCancel: (taskId: string) => void;
  onRemove: (taskId: string) => void;
  onNavigate?: (task: BackendTask) => void;
  onMarkViewed?: (taskId: string) => void;
  renderRowActions?: TaskRowActionsRenderer;
  /**
   * When provided, the row's primary click opens an external detail view
   * (typically `<TaskDetailDialog>` mounted by the parent panel) instead of
   * toggling the inline expand. Mark-viewed still fires for terminal-unviewed
   * tasks. Existing callers that omit this prop keep the legacy
   * "click toggles inline expand" behavior.
   */
  onShowDetails?: (taskId: string) => void;
}

const STATUS_COLORS: Record<string, string> = {
  pending: 'info.main',
  running: 'primary.main',
  retrying: 'warning.main',
  cancelling: 'warning.main',
  completed: 'success.main',
  error: 'error.main',
  cancelled: 'error.main',
  skipped: 'text.disabled',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  running: 'Running',
  retrying: 'Retrying',
  cancelling: 'Cancelling',
  completed: 'Completed',
  error: 'Error',
  cancelled: 'Cancelled',
  skipped: 'Skipped',
};

interface TaskBillingInfo {
  creditDebitMinor?: number;
  currency?: string;
}

function getTaskBilling(task: BackendTask): TaskBillingInfo | undefined {
  return (task as BackendTask & { billing?: TaskBillingInfo }).billing;
}

function formatMoneyMinor(amountMinor: number, currency = 'usd'): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amountMinor / 100);
}

export const TaskItem: React.FC<TaskItemProps> = ({
  task,
  isUnviewed,
  compact = false,
  showNavigation = true,
  onCancel,
  onRemove,
  onNavigate,
  onMarkViewed,
  onShowDetails,
  renderRowActions,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isActive = ACTIVE_STATUSES.includes(task.status);
  const isTerminal = TERMINAL_STATUSES.includes(task.status);
  const billing = getTaskBilling(task);

  // Sort phases by order for stepper
  const sortedPhases = useMemo(() => {
    if (!task.phases) return [];
    return Object.entries(task.phases)
      .sort(([, a], [, b]) => (a.order ?? 0) - (b.order ?? 0));
  }, [task.phases]);

  const handleClick = () => {
    if (isTerminal && isUnviewed && onMarkViewed) {
      onMarkViewed(task.id);
    }
    if (onShowDetails) {
      onShowDetails(task.id);
      return;
    }
    setExpanded((prev) => !prev);
  };

  return (
    <Box data-testid={`${TASKS_PANEL.itemPrefix}${task.id}`}>
      <ListItem
        dense
        sx={{
          px: 1,
          py: 0.5,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
          ...(isUnviewed && { fontWeight: 'bold' }),
        }}
        onClick={handleClick}
        secondaryAction={
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {isActive && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onCancel(task.id);
                }}
                data-testid={`${TASKS_PANEL.cancelPrefix}${task.id}`}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
            {isTerminal && showNavigation && task.targetEntityPath?.frontendUrl && onNavigate && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onNavigate(task);
                }}
                data-testid={`${TASKS_PANEL.navigatePrefix}${task.id}`}
              >
                <OpenInNewIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
            {isTerminal && renderRowActions?.(task)}
            {isTerminal && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(task.id);
                }}
                data-testid={`${TASKS_PANEL.removePrefix}${task.id}`}
              >
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
            {!compact && sortedPhases.length > 0 && (
              <IconButton
                size="small"
                data-testid={`${TASKS_PANEL.expandPrefix}${task.id}`}
              >
                {expanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            )}
          </Box>
        }
      >
        {/* Unviewed indicator */}
        {isUnviewed && (
          <CircleIcon
            sx={{ fontSize: 8, color: 'primary.main', mr: 1, flexShrink: 0 }}
            data-testid={`${TASKS_PANEL.unviewedDotPrefix}${task.id}`}
          />
        )}

        <ListItemText
          primary={
            <Typography
              variant="body2"
              noWrap
              sx={{
                fontWeight: isUnviewed ? 600 : 400,
                maxWidth: compact ? 160 : 240,
              }}
            >
              {task.action}
            </Typography>
          }
          secondary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              <Chip
                label={STATUS_LABELS[task.status] ?? task.status}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  bgcolor: STATUS_COLORS[task.status] ?? 'text.disabled',
                  color: 'white',
                }}
                data-testid={`${TASKS_PANEL.statusPrefix}${task.id}`}
              />
              {/* Mini phase stepper (compact) */}
              {compact && sortedPhases.length > 0 && (
                <Box
                  sx={{ display: 'flex', gap: 0.25, ml: 0.5 }}
                  data-testid={`${TASKS_PANEL.phaseStepperPrefix}${task.id}`}
                >
                  {sortedPhases.map(([key, phase]) => (
                    <Tooltip key={key} title={phase.displayName ?? phase.name}>
                      <CircleIcon
                        sx={{
                          fontSize: 6,
                          color:
                            phase.status === 'completed' ? 'success.main'
                            : phase.status === 'running' ? 'primary.main'
                            : phase.status === 'error' ? 'error.main'
                            : 'action.disabled',
                        }}
                        data-testid={`${TASKS_PANEL.phaseItemPrefix}${key}`}
                      />
                    </Tooltip>
                  ))}
                </Box>
              )}
            </Box>
          }
          secondaryTypographyProps={{ component: 'div' }}
        />
      </ListItem>

      {/* Running indicator */}
      {isActive && (
        <Box sx={{ px: 1 }}>
          <LinearProgress sx={{ height: 2 }} />
        </Box>
      )}

      {/* Expanded phase detail */}
      {!compact && (
        <Collapse in={expanded}>
          <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
            {task.description && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                {task.description}
              </Typography>
            )}
            {sortedPhases.length > 0 && (
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                data-testid={`${TASKS_PANEL.phaseStepperPrefix}${task.id}`}
              >
                {sortedPhases.map(([key, phase]) => (
                  <PhaseRow key={key} phaseKey={key} phase={phase} />
                ))}
              </Box>
            )}
            {task.error && (
              <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                Error: {task.error}
              </Typography>
            )}
            {task.usage && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Tokens: {task.usage.inputTokens?.toLocaleString()} in / {task.usage.outputTokens?.toLocaleString()} out
                {task.estimatedCostUsd != null && ` ($${task.estimatedCostUsd.toFixed(4)})`}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Started by {task.userId || 'unknown'}
              {billing ? ` · Credits debited: ${formatMoneyMinor(billing.creditDebitMinor || 0, billing.currency)}` : ''}
            </Typography>
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

// ── Phase row for expanded detail ──

const PhaseRow: React.FC<{ phaseKey: string; phase: TaskPhaseDefinition }> = ({
  phaseKey,
  phase,
}) => {
  const icon =
    phase.status === 'completed' ? '✓'
    : phase.status === 'running' ? '●'
    : phase.status === 'error' ? '✗'
    : phase.status === 'skipped' ? '—'
    : '○';

  const color =
    phase.status === 'completed' ? 'success.main'
    : phase.status === 'running' ? 'primary.main'
    : phase.status === 'error' ? 'error.main'
    : 'text.disabled';

  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
      data-testid={`${TASKS_PANEL.phaseItemPrefix}${phaseKey}`}
    >
      <Typography variant="caption" sx={{ color, minWidth: 12, textAlign: 'center' }}>
        {icon}
      </Typography>
      <Typography variant="caption" sx={{ color: phase.status === 'running' ? 'text.primary' : 'text.secondary' }}>
        {phase.displayName ?? phase.name}
      </Typography>
      {phase.error && (
        <Typography variant="caption" color="error" sx={{ ml: 0.5 }}>
          — {phase.error}
        </Typography>
      )}
    </Box>
  );
};
